// frontend/src/components/admin/ExtensionsManager.jsx

import { useState, useEffect, useRef } from 'react';
import {
  Zap, RefreshCw, AlertCircle, CheckCircle2, Edit3, X,
  Check, Building2, TrendingUp, ChevronRight,
  Search, ToggleLeft, ToggleRight, Info, HelpCircle,
  Package, DollarSign, BarChart2,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ============================================================================
// TOOLTIP COMPONENT
// ============================================================================
const Tooltip = ({ content, children }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
          transform: 'translateX(-50%)', zIndex: 9999,
          background: '#1e293b', color: '#f1f5f9',
          fontSize: 11, fontWeight: 500, lineHeight: 1.5,
          padding: '7px 10px', borderRadius: 8,
          whiteSpace: 'pre-line', maxWidth: 240, textAlign: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          pointerEvents: 'none',
        }}>
          {content}
          {/* Flèche */}
          <span style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid #1e293b',
          }} />
        </span>
      )}
    </span>
  );
};

// ============================================================================
// UTILS
// ============================================================================
const formatFCFA = (n) => Number(n || 0).toLocaleString('fr-FR') + ' FCFA';

// ============================================================================
// STAT CARD avec tooltip
// ============================================================================
const StatCard = ({ icon: Icon, label, value, color, tooltip, t }) => (
  <div style={{ flex: 1, padding: '18px 20px', borderRadius: 14, background: t.bgCard,
    border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{ width: 42, height: 42, borderRadius: 11, background: `${color}18`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={20} style={{ color }} />
    </div>
    <div style={{ flex: 1 }}>
      <p style={{ fontSize: 20, fontWeight: 900, color: t.text, margin: '0 0 2px', lineHeight: 1 }}>{value}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, margin: 0 }}>{label}</p>
        {tooltip && (
          <Tooltip content={tooltip}>
            <HelpCircle size={11} style={{ color: t.textMuted, cursor: 'help' }} />
          </Tooltip>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// MODAL — Détail extension par entreprise
// ============================================================================
const ExtensionDetailModal = ({ extension, onClose, theme: t }) => {
  const [companies,    setCompanies]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [actionKey,    setActionKey]    = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');

  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.extensions.getByExtension(extension.flag_key);
      if (res.success) setCompanies(res.data || []);
      else setError(res.message);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const handleActivate = async (company) => {
    setActionKey(company.id); setError('');
    try {
      const res = await superAdminApi.extensions.activate(company.id, extension.flag_key, extension.prix_defaut || 0);
      if (!res.success) { setError(res.message); return; }
      setSuccess(`Activé pour ${company.nom}`);
      setTimeout(() => setSuccess(''), 3000);
      await loadCompanies();
    } catch { setError('Erreur réseau'); }
    finally { setActionKey(null); }
  };

  const handleDeactivate = async (company) => {
    setActionKey(company.id); setError('');
    try {
      const res = await superAdminApi.extensions.deactivate(company.id, extension.flag_key);
      if (!res.success) { setError(res.message); return; }
      setSuccess(`Désactivé pour ${company.nom}`);
      setTimeout(() => setSuccess(''), 3000);
      await loadCompanies();
    } catch { setError('Erreur réseau'); }
    finally { setActionKey(null); }
  };

  const handleSavePrice = async (company) => {
    if (!editingPrice || editingPrice.companyId !== company.id) return;
    setActionKey(company.id); setError('');
    try {
      const res = await superAdminApi.extensions.updatePrice(company.id, extension.flag_key, parseFloat(editingPrice.value) || 0);
      if (!res.success) { setError(res.message); return; }
      setSuccess('Prix mis à jour');
      setTimeout(() => setSuccess(''), 3000);
      setEditingPrice(null);
      await loadCompanies();
    } catch { setError('Erreur réseau'); }
    finally { setActionKey(null); }
  };

  const activeCompanies = companies.filter(c => c.is_active);
  const totalMRR = activeCompanies.reduce((sum, c) => sum + (parseFloat(c.prix_mensuel) || 0), 0);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 700, maxHeight: '88vh', borderRadius: 20,
        background: t.bgCard, border: `1px solid ${t.border}`, overflow: 'hidden',
        display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${t.border}`,
          display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(245,158,11,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Zap size={20} style={{ color: '#f59e0b' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <p style={{ fontSize: 15, fontWeight: 800, color: t.text, margin: 0 }}>{extension.label}</p>
              <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 99,
                background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                border: '1px solid rgba(245,158,11,0.25)', textTransform: 'uppercase' }}>EXTENSION</span>
            </div>
            <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
              <strong style={{ color: '#22c55e' }}>{activeCompanies.length}</strong> entreprise{activeCompanies.length !== 1 ? 's' : ''} abonnée{activeCompanies.length !== 1 ? 's' : ''} —
              MRR : <strong style={{ color: '#f59e0b' }}>{formatFCFA(totalMRR)}</strong>
            </p>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 9, border: `1px solid ${t.border}`,
            background: t.bgInput, color: t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={15} />
          </button>
        </div>

        {/* Explication */}
        <div style={{ padding: '12px 24px', background: 'rgba(245,158,11,0.04)',
          borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
          <p style={{ fontSize: 12, color: t.textSecondary, margin: 0, lineHeight: 1.6 }}>
            Activez ou désactivez ce module pour chaque entreprise individuellement.
            Le prix affiché est le montant facturé <strong>en plus de la licence mensuelle</strong> de l'entreprise.
          </p>
        </div>

        {/* Alertes */}
        {(error || success) && (
          <div style={{ padding: '8px 24px', flexShrink: 0 }}>
            {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 7 }}>
              <AlertCircle size={13} style={{ color: '#ef4444', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
            </div>}
            {success && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', display: 'flex', gap: 7 }}>
              <CheckCircle2 size={13} style={{ color: '#22c55e', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#22c55e' }}>{success}</span>
            </div>}
          </div>
        )}

        {/* En-tête colonnes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 120px',
          padding: '8px 24px', background: t.bgTableHeader,
          borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Entreprise</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Prix / mois</span>
            <Tooltip content={"Montant facturé à l'entreprise\npour cette extension chaque mois.\nS'ajoute au prix de sa licence."}>
              <HelpCircle size={10} style={{ color: t.textMuted, cursor: 'help' }} />
            </Tooltip>
          </div>
          <span style={{ fontSize: 10, fontWeight: 800, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'right' }}>Statut</span>
        </div>

        {/* Liste */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <RefreshCw size={16} style={{ color: t.primary, animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 12, color: t.textMuted }}>Chargement…</span>
            </div>
          ) : companies.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p style={{ fontSize: 13, color: t.textMuted }}>Aucune entreprise trouvée.</p>
            </div>
          ) : companies.map((company) => {
            const isActive   = !!company.is_active;
            const isEditing  = editingPrice?.companyId === company.id;
            const isBusy     = actionKey === company.id;
            const viaLicence = !!company.licence_has_flag;

            return (
              <div key={company.id} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 120px',
                padding: '12px 24px', borderBottom: `1px solid ${t.borderLight}`,
                alignItems: 'center',
                background: isActive ? 'rgba(245,158,11,0.02)' : 'transparent' }}>

                {/* Entreprise */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    background: isActive ? '#22c55e' : t.borderLight }} />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0 }}>{company.nom}</p>
                      {viaLicence && (
                        <Tooltip content={`Ce module est déjà inclus dans\nla licence "${company.plan_type}" de cette entreprise.\nPas de facturation séparée.`}>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
                            background: 'rgba(139,92,246,0.1)', color: '#8b5cf6',
                            border: '1px solid rgba(139,92,246,0.2)', cursor: 'help' }}>
                            Inclus licence
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
                      Plan {company.plan_type || '—'}
                    </p>
                  </div>
                </div>

                {/* Prix */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {isActive && !viaLicence ? (
                    isEditing ? (
                      <>
                        <input type="number" min="0" value={editingPrice.value}
                          onChange={e => setEditingPrice({ companyId: company.id, value: e.target.value })}
                          autoFocus
                          style={{ width: 80, padding: '5px 8px', borderRadius: 7, background: t.bgInput,
                            border: '1px solid #f59e0b', color: t.text, fontSize: 12, fontWeight: 700,
                            outline: 'none', fontFamily: 'inherit' }} />
                        <button onClick={() => handleSavePrice(company)} disabled={isBusy}
                          style={{ width: 26, height: 26, borderRadius: 7, border: 'none', background: '#22c55e',
                            color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isBusy ? <span style={{ width: 10, height: 10, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> : <Check size={12} />}
                        </button>
                        <button onClick={() => setEditingPrice(null)}
                          style={{ width: 26, height: 26, borderRadius: 7, border: `1px solid ${t.border}`,
                            background: t.bgInput, color: t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <X size={12} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#f59e0b' }}>
                          {company.prix_mensuel > 0 ? `${Number(company.prix_mensuel).toLocaleString('fr-FR')} F` : 'Gratuit'}
                        </span>
                        <button onClick={() => setEditingPrice({ companyId: company.id, value: company.prix_mensuel || 0 })}
                          style={{ width: 24, height: 24, borderRadius: 6, border: `1px solid ${t.border}`,
                            background: t.bgInput, color: t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Edit3 size={10} />
                        </button>
                      </>
                    )
                  ) : viaLicence ? (
                    <span style={{ fontSize: 11, color: '#8b5cf6', fontStyle: 'italic' }}>Inclus</span>
                  ) : (
                    <span style={{ fontSize: 11, color: t.textMuted }}>—</span>
                  )}
                </div>

                {/* Toggle */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {viaLicence ? (
                    <span style={{ fontSize: 10, color: '#8b5cf6', fontStyle: 'italic' }}>Auto</span>
                  ) : (
                    <button onClick={() => isActive ? handleDeactivate(company) : handleActivate(company)}
                      disabled={isBusy}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 8,
                        border: `1px solid ${isActive ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.25)'}`,
                        background: isActive ? 'rgba(239,68,68,0.06)' : 'rgba(34,197,94,0.06)',
                        color: isActive ? '#ef4444' : '#22c55e',
                        fontSize: 11, fontWeight: 700, cursor: isBusy ? 'wait' : 'pointer' }}>
                      {isBusy
                        ? <span style={{ width: 11, height: 11, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block', opacity: 0.6 }} />
                        : isActive ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                      {isActive ? 'Désactiver' : 'Activer'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const ExtensionsManager = () => {
  const t = useTheme();

  const [extensions,  setExtensions]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [search,      setSearch]      = useState('');
  const [selected,    setSelected]    = useState(null);
  const [editingPrix, setEditingPrix] = useState(null);
  const [savingPrix,  setSavingPrix]  = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.extensions.getAll();
      if (res.success) setExtensions(res.data || []);
      else setError(res.message);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const handleSavePrixDefaut = async (flagKey) => {
    if (!editingPrix || editingPrix.flagKey !== flagKey) return;
    setSavingPrix(flagKey); setError('');
    try {
      const res = await superAdminApi.licences.setExtension(flagKey, true, parseFloat(editingPrix.value) || 0);
      if (!res.success) { setError(res.message); return; }
      setSuccess('Prix de référence mis à jour');
      setTimeout(() => setSuccess(''), 3000);
      setEditingPrix(null);
      await loadData();
    } catch { setError('Erreur réseau'); }
    finally { setSavingPrix(null); }
  };

  const handleUnsetExtension = async (flagKey) => {
    try {
      const res = await superAdminApi.licences.setExtension(flagKey, false, 0);
      if (!res.success) { setError(res.message); return; }
      setSuccess('Flag remis dans la matrice des licences');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch { setError('Erreur réseau'); }
  };

  const totalAbonnements = extensions.reduce((s, e) => s + (e.nb_actives || 0), 0);
  const totalMRR         = extensions.reduce((s, e) => s + (parseFloat(e.mrr) || 0), 0);

  const filtered = extensions.filter(e =>
    !search || e.label?.toLowerCase().includes(search.toLowerCase()) || e.flag_key?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <RefreshCw size={20} style={{ color: t.primary, animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: 13, color: t.textMuted }}>Chargement…</span>
    </div>
  );

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Zap size={20} style={{ color: '#f59e0b' }} />
            <h1 style={{ fontSize: 18, fontWeight: 800, color: t.text, margin: 0 }}>Extensions payantes</h1>
          </div>
          <p style={{ fontSize: 13, color: t.textMuted, margin: 0, maxWidth: 600 }}>
            Modules hors-licence, activables et facturés individuellement par entreprise.
          </p>
        </div>
        <button onClick={loadData} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
          borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`,
          color: t.textMuted, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <RefreshCw size={13} /> Actualiser
        </button>
      </div>

      {/* BANDEAU EXPLICATION */}
      <div style={{ padding: '16px 20px', borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.04))',
        border: '1px solid rgba(245,158,11,0.2)' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Info size={16} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '0 0 6px' }}>
              À quoi sert cet écran ?
            </p>
            <p style={{ fontSize: 12, color: t.textSecondary, margin: '0 0 8px', lineHeight: 1.7 }}>
              Certains modules ne font pas partie des licences standards (Basique / Standard / Premium).
              Ils peuvent être activés <strong>à la demande</strong> pour une entreprise spécifique, 
              moyennant un <strong>supplément mensuel</strong> qui s'ajoute au prix de sa licence.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {[
                { icon: '🏷️', text: 'Prix de référence = prix suggéré à l\'activation, modifiable par entreprise' },
                { icon: '📦', text: 'co. = nombre d\'entreprises actuellement abonnées à ce module' },
                { icon: '📈', text: 'MRR = revenu mensuel récurrent généré par ce module (somme des prix actifs)' },
              ].map(item => (
                <div key={item.text} style={{ display: 'flex', alignItems: 'flex-start', gap: 6,
                  padding: '6px 10px', borderRadius: 8, background: 'rgba(245,158,11,0.06)',
                  border: '1px solid rgba(245,158,11,0.15)', flex: '1 1 220px' }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ fontSize: 11, color: t.textSecondary, lineHeight: 1.5 }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ALERTES */}
      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 8 }}>
          <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', display: 'flex', gap: 8 }}>
          <CheckCircle2 size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#22c55e' }}>{success}</span>
        </div>
      )}

      {/* STATS */}
      <div style={{ display: 'flex', gap: 14 }}>
        <StatCard
          icon={Package} color="#f59e0b" t={t}
          label="Extensions configurées"
          value={`${extensions.length} modules`}
          tooltip={"Nombre total de modules\nmarqués comme extensions payantes\ndans le catalogue de flags."}
        />
        <StatCard
          icon={Building2} color="#22c55e" t={t}
          label="Souscriptions totales"
          value={`${totalAbonnements} co.`}
          tooltip={"co. = entreprises (companies)\nNombre total d'entreprises\nayant au moins une extension active.\nUne même entreprise peut avoir\nplusieurs extensions."}
        />
        <StatCard
          icon={TrendingUp} color="#3b82f6" t={t}
          label="MRR extensions estimé"
          value={totalMRR > 0 ? formatFCFA(totalMRR) : '—'}
          tooltip={"MRR = Monthly Recurring Revenue\n(Revenu Mensuel Récurrent)\nSomme de tous les prix d'extensions\nactives sur toutes les entreprises.\nEstimation du revenu mensuel\ngénéré par les extensions seules."}
        />
      </div>

      {/* BARRE RECHERCHE */}
      <div style={{ position: 'relative' }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none' }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher une extension…"
          style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 10, background: t.bgInput,
            border: `1px solid ${t.border}`, color: t.text, fontSize: 13, outline: 'none',
            boxSizing: 'border-box', fontFamily: 'inherit' }}
          onFocus={e => e.target.style.borderColor = t.primary}
          onBlur={e  => e.target.style.borderColor = t.border} />
      </div>

      {/* LISTE */}
      {filtered.length === 0 ? (
        <div style={{ padding: '50px 20px', textAlign: 'center', borderRadius: 16, border: `1px solid ${t.border}` }}>
          <Zap size={32} style={{ color: t.textMuted, marginBottom: 12 }} />
          <p style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: '0 0 6px' }}>
            {search ? 'Aucune extension trouvée' : 'Aucune extension configurée'}
          </p>
          <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>
            {search ? 'Modifiez votre recherche.' : 'Dans le LicenceManager, cliquez sur "Ext." sur un flag pour le convertir en extension.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((ext) => {
            const isEditingPrix = editingPrix?.flagKey === ext.flag_key;
            const isSaving      = savingPrix === ext.flag_key;

            return (
              <div key={ext.flag_key} style={{ borderRadius: 14, border: '1px solid rgba(245,158,11,0.2)',
                background: t.bgCard, overflow: 'hidden' }}>

                {/* Ligne principale */}
                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(245,158,11,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Zap size={16} style={{ color: '#f59e0b' }} />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0 }}>{ext.label}</p>
                      <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 99,
                        background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                        border: '1px solid rgba(245,158,11,0.25)', textTransform: 'uppercase' }}>EXTENSION</span>
                    </div>
                    {ext.description && <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{ext.description}</p>}
                    <code style={{ fontSize: 9, color: t.textMuted, background: t.bgInput,
                      padding: '1px 5px', borderRadius: 4, marginTop: 3, display: 'inline-block' }}>{ext.flag_key}</code>
                  </div>
                </div>

                {/* Barre de stats */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                  borderTop: '1px solid rgba(245,158,11,0.1)' }}>

                  {/* Abonnées */}
                  <div style={{ padding: '12px 20px', borderRight: '1px solid rgba(245,158,11,0.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Abonnées</span>
                      <Tooltip content={"Nombre d'entreprises\nayant ce module activé et payant."}>
                        <HelpCircle size={10} style={{ color: t.textMuted, cursor: 'help' }} />
                      </Tooltip>
                    </div>
                    <p style={{ fontSize: 16, fontWeight: 800, color: ext.nb_actives > 0 ? '#22c55e' : t.textMuted, margin: 0 }}>
                      {ext.nb_actives || 0} <span style={{ fontSize: 11, fontWeight: 600 }}>co.</span>
                    </p>
                  </div>

                  {/* Prix référence */}
                  <div style={{ padding: '12px 20px', borderRight: '1px solid rgba(245,158,11,0.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Prix référence</span>
                      <Tooltip content={"Prix suggéré pré-rempli\nlors de l'activation.\nModifiable entreprise par entreprise."}>
                        <HelpCircle size={10} style={{ color: t.textMuted, cursor: 'help' }} />
                      </Tooltip>
                    </div>
                    {isEditingPrix ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <input type="number" min="0" value={editingPrix.value}
                          onChange={e => setEditingPrix({ flagKey: ext.flag_key, value: e.target.value })}
                          autoFocus
                          style={{ width: 80, padding: '4px 7px', borderRadius: 7, background: t.bgInput,
                            border: '1px solid #f59e0b', color: t.text, fontSize: 12, fontWeight: 700,
                            outline: 'none', fontFamily: 'inherit' }} />
                        <button onClick={() => handleSavePrixDefaut(ext.flag_key)} disabled={isSaving}
                          style={{ width: 24, height: 24, borderRadius: 6, border: 'none', background: '#22c55e',
                            color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isSaving ? <span style={{ width: 10, height: 10, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> : <Check size={11} />}
                        </button>
                        <button onClick={() => setEditingPrix(null)}
                          style={{ width: 24, height: 24, borderRadius: 6, border: `1px solid ${t.border}`,
                            background: t.bgInput, color: t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <X size={11} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <p style={{ fontSize: 14, fontWeight: 800, color: ext.prix_defaut > 0 ? '#f59e0b' : t.textMuted, margin: 0 }}>
                          {ext.prix_defaut > 0 ? `${Number(ext.prix_defaut).toLocaleString('fr-FR')} F` : 'Libre'}
                        </p>
                        <button onClick={() => setEditingPrix({ flagKey: ext.flag_key, value: ext.prix_defaut || 0 })}
                          style={{ width: 22, height: 22, borderRadius: 6, border: `1px solid ${t.border}`,
                            background: t.bgInput, color: t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Edit3 size={10} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* MRR */}
                  <div style={{ padding: '12px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>MRR estimé</span>
                      <Tooltip content={"MRR = Monthly Recurring Revenue\nRevenu mensuel récurrent.\nSomme des prix payés par\ntoutes les entreprises abonnées\nà cette extension ce mois-ci."}>
                        <HelpCircle size={10} style={{ color: t.textMuted, cursor: 'help' }} />
                      </Tooltip>
                    </div>
                    <p style={{ fontSize: 14, fontWeight: 800, color: ext.mrr > 0 ? '#3b82f6' : t.textMuted, margin: 0 }}>
                      {ext.mrr > 0 ? formatFCFA(ext.mrr) : '—'}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(245,158,11,0.1)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'rgba(245,158,11,0.02)' }}>
                  <button onClick={() => setSelected(ext)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 9,
                      background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)',
                      color: '#f59e0b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    <Building2 size={13} /> Gérer par entreprise <ChevronRight size={12} />
                  </button>
                  <button onClick={() => handleUnsetExtension(ext.flag_key)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 9,
                      border: `1px solid ${t.border}`, background: t.bgInput,
                      color: t.textMuted, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    <Package size={11} /> Remettre en licence
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* NOTE BAS */}
      <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(245,158,11,0.06)',
        border: '1px solid rgba(245,158,11,0.2)', display: 'flex', gap: 10 }}>
        <TrendingUp size={14} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: 12, color: t.textSecondary, margin: 0, lineHeight: 1.6 }}>
          Le <strong style={{ color: '#f59e0b' }}>MRR estimé</strong> est calculé sur la somme des prix par entreprise.
          Modifier le <strong style={{ color: '#f59e0b' }}>prix de référence</strong> pré-remplit les nouvelles activations sans impacter les prix déjà en place.
        </p>
      </div>

      {selected && (
        <ExtensionDetailModal extension={selected} onClose={() => setSelected(null)} theme={t} />
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default ExtensionsManager;